import * as React from "react";
import { useEffect, useRef } from "react";
import { Pagination, PaginationItem, useMediaQuery } from "@mui/material";
import { useDeleteAccountInfoMutation } from "../../../Services/customer/FrontendCustomer";
import { useNavigate } from "react-router-dom";
import ReactModal from "react-modal";
import LoadingSpinner from "../../LoadingSpinner";
import { formatDate } from "../../reusableComponent/DateFormat";
import DeleteContactModal from "../contact/DeleteContactModal";
import EmptyRows from "../../reusableComponent/EmptyRows";
import { useState } from "react";
import SortingTableContact from "../../reusableComponent/SortingTableContact";

const SCROLL_POSITION_KEY = "accountCardsScrollPosition";
const LAST_VIEWED_ACCOUNT_KEY = "lastViewedAccount";

interface IPROPS {
  accountCardLoading: any;
  accountData: any;
  totalCount: any;
  refetch: any;
  setPage: any;
  page: any;
  onPageSizeChange: (newPageSize: number) => void; // Add this prop
  pageSize: number; // Add this prop
  setLiftingColumn: any;
}

const AccountCards: React.FC<IPROPS> = ({
  accountCardLoading,
  accountData,
  totalCount,
  refetch,
  setPage,
  page,
  onPageSizeChange, // Add this
  pageSize, // Add this
  setLiftingColumn,
}) => {
  const isLargeScreen = useMediaQuery("(max-width:825px)");
  const isSmallScreen = useMediaQuery("(max-width:575px)");
  const isPageScreen = useMediaQuery("(max-width:430px)");
  const [accountsData, setAccountData] = React.useState<any[]>([]);
  const [deleteButtonClicked, setDeleteButtonClicked] =
    React.useState<boolean>(false);
  const [storedData, setStoredData] = React.useState<any>(null);
  const [isEditAccountDetailsEnable, setIsEditAccountDetailsEnable] =
    useState(false);
  const tableHeader = ["Accounts", "Created date", "Tag", "Action"];

  const [trigger] = useDeleteAccountInfoMutation();

  const navigate = useNavigate();

  // Refs for scroll position management (same pattern as TicketListView)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  // Store scroll position when navigating away (use the element that actually scrolls)
  const storeScrollPosition = (accountId: string) => {
    const scrollableContainer = scrollContainerRef.current;
    if (scrollableContainer) {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        String(scrollableContainer.scrollTop)
      );
    }
    sessionStorage.setItem(LAST_VIEWED_ACCOUNT_KEY, accountId);
  };

  // Restore scroll position when returning (retry until content is laid out)
  const restoreScrollPosition = (): (() => void) => {
    const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (!savedScrollPosition) return () => {};

    const targetScrollTop = parseInt(savedScrollPosition, 10);
    if (Number.isNaN(targetScrollTop) || targetScrollTop < 0) return () => {};

    const applyScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = targetScrollTop;
    };

    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(applyScroll, d));
    requestAnimationFrame(() => {
      requestAnimationFrame(applyScroll);
    });
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
    sessionStorage.removeItem(LAST_VIEWED_ACCOUNT_KEY);
  };

  // Restore scroll position when list is visible (not loading, has results)
  useEffect(() => {
    const canRestore =
      !accountCardLoading &&
      accountData &&
      accountData.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function") {
          scrollRestoreCleanupRef.current = cleanup;
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        if (scrollRestoreCleanupRef.current) {
          scrollRestoreCleanupRef.current();
          scrollRestoreCleanupRef.current = null;
        }
      };
    }
  }, [accountCardLoading, accountData]);

  // Store scroll position before page unload (e.g. refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const scrollableContainer = scrollContainerRef.current;
      if (scrollableContainer) {
        sessionStorage.setItem(
          SCROLL_POSITION_KEY,
          String(scrollableContainer.scrollTop)
        );
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const deleteHandler = (row: any) => {
    setDeleteButtonClicked(true);
    setStoredData(row);
  };

  const closeDeleteModal = () => {
    setDeleteButtonClicked(false);
  };

  const editHandler = (row: any) => {
    if (row?.id != null) storeScrollPosition(String(row.id));
    setIsEditAccountDetailsEnable(false);
    localStorage.setItem("isEditAccountDetailsEnable", JSON.stringify(false));
    navigate(`/customer/info/${row.id}`, { state: row });
  };

  const editAccountEnable = (row: any) => {
    if (row?.id != null) storeScrollPosition(String(row.id));
    setIsEditAccountDetailsEnable(true);
    localStorage.setItem("isEditAccountDetailsEnable", JSON.stringify(true));
    navigate(`/customer/info/${row.id}`, { state: row });
  };

  const customModalStyles1: ReactModal.Styles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "740px",
    },
  };

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    clearScrollPosition();
  };

  const handleRowsPerPageChange = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onPageSizeChange(newRowsPerPage); // Use the prop function instead of local state
    localStorage.setItem("accountRowsPerPage", newRowsPerPage.toString());
    clearScrollPosition();
  };

  // Load rows per page from localStorage on component mount
  React.useEffect(() => {
    const savedRowsPerPage = localStorage.getItem("accountRowsPerPage");
    if (savedRowsPerPage) {
      const savedSize = parseInt(savedRowsPerPage, 10);
      if (savedSize !== pageSize) {
        onPageSizeChange(savedSize);
      }
    }
  }, []);

  const getRandomColor = () => {
    const colors = [
      "bg-primary",
      "bg-success",
      "bg-danger",
      "bg-warning",
      "bg-dark",
    ];
    // Create a Uint32Array with a single element
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const randomIndex = array[0] % colors.length;
    return colors[randomIndex];
  };

  const getInitials = (name: string) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    return (
      nameParts[0].charAt(0).toUpperCase() +
      nameParts[1].charAt(0).toUpperCase()
    );
  };

  const totalPages = Math.ceil(totalCount / pageSize); // Use pageSize prop instead of local state
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  const pageSizeOptions = [10, 25, 50, 100];
  const fontSize = isLargeScreen ? "13px" : "15px";

  /* ── shared td style (white card look) ── */
  const tdBase: React.CSSProperties = {
    padding: "12px 16px",
    whiteSpace: "nowrap",
    fontSize,
    verticalAlign: "middle",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    borderTop: "1px solid #f0f0f0",
    borderBottom: "1px solid #f0f0f0",
  };

  const tdFirst: React.CSSProperties = {
    ...tdBase,
    paddingLeft: "20px",
    borderLeft: "1px solid #f0f0f0",
    borderRadius: "6px 0 0 6px",
  };

  const tdLast: React.CSSProperties = {
    ...tdBase,
    borderRight: "1px solid #f0f0f0",
    borderRadius: "0 6px 6px 0",
    textAlign: "center",
  };

  return (
    <>
      {/* ── Scrollable table container ── */}
      <div
        ref={scrollContainerRef}
        style={{ overflow: "auto", height: "calc(100vh - 355px)" }}
      >
        <table
          style={{
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 6px",   /* vertical gap between rows = card gap */
          }}
        >
          {/* ════ HEADER ════ */}
          <thead>
            <tr>
              {tableHeader.map((el, index) => (
                <th
                  key={index}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#E4E4E4",
                    padding: "10px 16px",
                    paddingLeft: index === 0 ? "20px" : "16px",
                    whiteSpace: "nowrap",
                    textAlign: el === "Action" ? "center" : "left",
                    fontSize,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    borderRadius:
                      index === 0
                        ? "6px 0 0 6px"
                        : index === tableHeader.length - 1
                        ? "0 6px 6px 0"
                        : "0",
                    minWidth:
                      el === "Action"         ? "130px"
                      : el === "Accounts"     ? "220px"
                      : el === "Created date" ? "180px"
                      : el === "Tag"         ? "150px"
                      :                         "150px",
                  }}
                >
                  <strong
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      justifyContent: el === "Action" ? "center" : "flex-start",
                    }}
                  >
                    {el}
                    {el !== "Tag" && el !== "Action" && (
                      <SortingTableContact
                        label={
                          el === "Accounts"
                            ? "name"
                            : el === "Created date"
                              ? "created"
                              : el
                        }
                        setLiftingColumn={setLiftingColumn}
                      />
                    )}
                  </strong>
                </th>
              ))}
            </tr>
          </thead>

          {/* ════ BODY ════ */}
          <tbody>
            {accountCardLoading ? (
              <tr>
                <td colSpan={4} style={{ padding: "40px", textAlign: "center" }}>
                  <LoadingSpinner />
                </td>
              </tr>
            ) : !accountData?.length ? (
              <tr>
                <td colSpan={4}>
                  <EmptyRows value="" />
                </td>
              </tr>
            ) : (
              accountData.map((row: any, index: number) => (
                <tr
                  key={index}
                  onClick={() => editHandler(row)}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    Array.from(e.currentTarget.cells).forEach(
                      (td) => ((td as HTMLElement).style.backgroundColor = "#f9f9f9")
                    );
                  }}
                  onMouseLeave={(e) => {
                    Array.from(e.currentTarget.cells).forEach(
                      (td) => ((td as HTMLElement).style.backgroundColor = "#ffffff")
                    );
                  }}
                >
                  {/* Accounts */}
                  <td style={tdFirst}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div className="symbol symbol-25px symbol-circle fs-2">
                        <span
                          className={`symbol-label ${getRandomColor()} text-inverse-warning fw-bold`}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(row?.name)}
                        </span>
                      </div>
                      <label htmlFor="" style={{ fontSize, marginBottom: 0, cursor: "pointer" }}>
                        {capitalizeFirstLetter(row?.name)}
                      </label>
                    </div>
                  </td>

                  {/* Created date */}
                  <td style={tdBase}>
                    <label htmlFor="" style={{ fontSize, marginBottom: 0, cursor: "pointer" }}>
                      {formatDate(row.created)}
                    </label>
                  </td>

                  {/* Tags */}
                  <td style={tdBase}>
                    <label htmlFor="" style={{ fontSize, marginBottom: 0, cursor: "pointer" }}>
                      {capitalizeFirstLetter(
                        row.tags?.map((tag: any) => tag.name).join(", ")
                      )}
                    </label>
                  </td>

                  {/* Action */}
                  <td style={tdLast}>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <button
                        className="text-hover-primary border-0 me-4"
                        onClick={(event) => {
                          event.stopPropagation();
                          editAccountEnable(row);
                        }}
                        title={!isEditAccountDetailsEnable ? "Edit" : "Disable"}
                        style={{ background: "transparent" }}
                      >
                        <i
                          data-placement="top"
                          title={!isEditAccountDetailsEnable ? "Edit" : "Disable"}
                          className="bi bi-pencil text-dark fs-4"
                          style={{
                            cursor: "pointer",
                            background: "transparent",
                          }}
                        ></i>
                      </button>

                      <button
                        className="text-hover-danger border-0 bg-white me-2"
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteHandler(row);
                        }}
                      >
                        <i
                          data-placement="top"
                          title="Delete"
                          className="bi bi-trash text-dark fs-4"
                        ></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReactModal
        isOpen={deleteButtonClicked}
        onRequestClose={closeDeleteModal}
        style={customModalStyles1}
        shouldCloseOnOverlayClick={false}
      >
        <DeleteContactModal
          closeModal={closeDeleteModal}
          setFieldsData={setAccountData}
          fieldsData={accountsData}
          storingFieldData={storedData}
          refetch={refetch}
          trigger={trigger}
        />
      </ReactModal>
  

        {/* Pagination Controls */}
      <div className={`d-flex justify-content-between align-items-center mt-4 px-3 mb-2 ${isPageScreen ? "flex-column gap-3" : ""}`}>
        {/* Left Side: Rows per page + range info */}
        <div className="d-flex align-items-center gap-3">
          <span style={{ fontSize: isSmallScreen ? "12px" : "14px" }}>Show</span>

          <select
            className="form-select form-select-sm"
            style={{ width: "60px", fontSize: isSmallScreen ? "12px" : "14px", padding: isSmallScreen ? "2px 4px" : "2px 6px" }}
            value={pageSize}
            onChange={handleRowsPerPageChange}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <span style={{ fontSize: isSmallScreen ? "12px" : "14px" }}>per page</span>
        </div>

        {/* Right Side: Pagination */}
        <Pagination
          sx={{
            "& .MuiPaginationItem-root": {
              fontSize: isSmallScreen ? "11px" : "14px",
              minWidth: isSmallScreen ? "28px" : "36px",
              height: isSmallScreen ? "28px" : "36px",
              margin: isSmallScreen ? "0 1px" : "0 2px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              color: "#374151",
              "&:hover": {
                backgroundColor: "#f9fafb",
                borderColor: "#d1d5db",
              },
              "&.Mui-selected": {
                backgroundColor: "#6366f1",
                color: "#ffffff",
                borderColor: "#6366f1",
                "&:hover": {
                  backgroundColor: "#4f46e5",
                  borderColor: "#4f46e5",
                },
              },
            },
          }}
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          renderItem={(item) =>
            item.type === "page" &&
            (Math.abs(item.page - page) <= 1 ||
              item.page === 1 ||
              item.page === totalPages) ? (
              <PaginationItem {...item} />
            ) : item.type !== "page" ? (
              <PaginationItem {...item} />
            ) : null
          }
        />
      </div>
      </>
  );
};

export default AccountCards;